import { RotateCcw } from "lucide-react";

import { PullSkillsPopover } from "./PullSkillsPopover";
import { ShareSkillCollectionButton } from "./ShareSkillCollectionButton";
import { SkillCollectionSaveButton } from "./SkillCollectionSaveButton";
import type { SkillCollectionSaveAction } from "./editor";

import { Button } from "@/components/ui/button";
import { BasicTooltip } from "@/components/ui/Tooltip";
import type { SkillCollectionVersionRecord } from "@/queries/skill-collections/types";

interface SkillCollectionHeaderActionsProps {
  isEditing: boolean;
  isSkillCollectionEditDisabled: boolean;
  saveAction: SkillCollectionSaveAction | null;
  saveButtonTooltip: string;
  onSaveActionClick: () => void;
  selectedVersion: SkillCollectionVersionRecord | null;
  latestVersionId?: string;
  latestVersionNumber?: number;
  collectionName: string;
  isRestoring: boolean;
  onRequestRollback: (version: SkillCollectionVersionRecord) => void;
}

export const SkillCollectionHeaderActions = ({
  isEditing,
  isSkillCollectionEditDisabled,
  saveAction,
  saveButtonTooltip,
  onSaveActionClick,
  selectedVersion,
  latestVersionId,
  latestVersionNumber,
  collectionName,
  isRestoring,
  onRequestRollback,
}: SkillCollectionHeaderActionsProps) => {
  if (isEditing) {
    return (
      <SkillCollectionSaveButton
        disabled={
          isSkillCollectionEditDisabled || !saveAction?.isDirty || saveAction?.isSaving === true
        }
        hasUnsavedChanges={saveAction?.isDirty ?? false}
        isSaving={saveAction?.isSaving ?? false}
        onClick={onSaveActionClick}
        tooltipContent={saveButtonTooltip}
      />
    );
  }

  if (!selectedVersion) {
    return undefined;
  }

  return (
    <div className="flex items-center gap-2">
      <PullSkillsPopover
        collectionName={collectionName}
        versionNumber={selectedVersion.number}
        provider={selectedVersion.provider}
      />
      <ShareSkillCollectionButton
        collectionName={collectionName}
        selectedVersionNumber={selectedVersion.number}
        latestVersionNumber={latestVersionNumber}
      />
      <BasicTooltip
        content={
          selectedVersion.id === latestVersionId
            ? "You are already on the latest version"
            : "Rollback to this version"
        }
      >
        <span>
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="shrink-0"
            onClick={() => onRequestRollback(selectedVersion)}
            disabled={selectedVersion.id === latestVersionId || isRestoring}
            isLoading={isRestoring}
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            Rollback
          </Button>
        </span>
      </BasicTooltip>
    </div>
  );
};
